// Only reviewed skill combinations are labelled; absence means not reviewed here.
const ROOT_IDENTITIES = new Set([
  "MaturaBasic_NumericConjugateRadicalSquaresDifference",
  "MaturaBasic_SquaresOfOppositeRadicalDifferences",
  "MaturaBasic_SquareSumTwoRadicalTerms",
  "MaturaRootBinomialClosedTask",
  "MaturaSqrtDifferenceSquareClosedTask",
]);
const INTEGER_POWERS = new Set([
  "MaturaBasic_SameBasePowerQuotient",
  "MaturaBasic_ThreePowerBasesProductQuotient",
  "MaturaPowerDivisionClosedTask",
  "MaturaBasic_ReciprocalPowerTimesPowerCommonBase",
  "MaturaBasic_NegativePowersFractionProduct",
  "MaturaPowerFactorOutClosedTask",
]);
const POWER_ROOTS = new Set([
  "MaturaNestedRootPowerClosedTask",
  "MaturaBasic_RootReciprocalNegativePower",
  "MaturaBasic_DecimalFractionPowerProductRoot",
  "MaturaBasic_PowerTimesSquareRootCubeRoot",
  "MaturaPowerTimesRootClosedTask",
  "MaturaBasic_IntegerPowerTimesRationalPowerCommonBase",
  "MaturaBasic_SymbolicSquareCubeSixthRootProduct",
  "MaturaBasic_NegativeHalfPowerSquareRational",
  "MaturaBasic_negative-exponents-and-square-root-quotient",
]);
const LOGARITHM_TYPES = [
 "MaturaBasic_same-base-log-product-quotient", "MaturaBasic_LogarithmOfRootPower",
 "MaturaLogPropClosedTask", "MaturaBasic_LogarithmRootOfBaseRoot", "MaturaLogCoefSumTruthClosedTask",
 "MaturaLogSingleCoefClosedTask", "MaturaBasic_LogarithmSumWeightedProduct",
 "MaturaBasic_LogSumTwoEquivalentIdentities", "MaturaBasic_LogarithmSquareRootBaseOfPower",
 "MaturaBasic_LogOneMinusWeightedSquareBaseLog",
];
// Topic access and the visible explanation share one reviewed definition.
const REVIEWED_SKILLS = Object.freeze({
  MaturaBasic_SumPolynomialCoefficientsUsingValueOne: {
    topicCodes:["2.7.2"],labels:["wartość wielomianu", "suma współczynników"],pointShares:{"2.7.2":1},
    note:"Suma współczynników jest równa W(1); nie trzeba rozwijać całego iloczynu.",
  },
  MaturaBasic_PolynomialFactorizationAndRootTruth: {
    topicCodes:["2.7.2"],labels:["mnożenie wielomianów", "wartość wielomianu"],pointShares:{"2.7.2":1},
  },
  MaturaBasic_CubicGroupingFactorizationClosed: {
    topicCodes:["2.7.2"],labels:["mnożenie wielomianów", "sprawdzanie postaci iloczynowej"],pointShares:{"2.7.2":1},
    note:"Podane odpowiedzi można sprawdzić przez wymnożenie nawiasów; samodzielne grupowanie nie jest konieczne.",
  },
  MaturaBasic_PolynomialGivenLinearFactorParameter: {
    topicCodes:["2.7.2"],labels:["wartość wielomianu", "podstawienie"],pointShares:{"2.7.2":1},
    note:"Podany czynnik x+1 pozwala podstawić x=-1 i skorzystać z W(-1)=0 bez dzielenia wielomianów.",
  },
  MaturaBasic_SubtractPolynomialProduct: {
    topicCodes:["2.7.2"], labels:["mnożenie wielomianów", "redukcja wyrazów podobnych"],
    pointShares:{"2.7.2":1},
    note:"Odejmowanie iloczynu wielomianów; wzór skróconego mnożenia nie jest potrzebny.",
  },
  ...Object.fromEntries(LOGARITHM_TYPES.map(type=>[type,{
    topicCodes:["1.5.2"],labels:["logarytmy"],pointShares:{"1.5.2":1},
  }])) ,
  MaturaBasic_NegativeLogarithmicFunctionRootArgument: {
    topicCodes:["1.5.2","3.5.2"],labels:["logarytmy","funkcja logarytmiczna"],pointShares:{"1.5.2":1},
  },
  MaturaBasic_LogarithmicFunctionValueMonotonicityTruth: {
    topicCodes:["3.5.2","1.5.2"],labels:["funkcja logarytmiczna","logarytmy"],pointShares:{"3.5.2":1},
  },
  ...Object.fromEntries([...INTEGER_POWERS].map(type => [type, {
    topicCodes:["1.4.3"], labels:["potęgi"], pointShares:{"1.4.3":1},
  }])) ,
  ...Object.fromEntries([...ROOT_IDENTITIES].map(type => [type, {
    topicCodes:["1.1.5", "2.1.2"], labels:["pierwiastki", "wzory skróconego mnożenia"],
  }])),
  ...Object.fromEntries([...POWER_ROOTS].map(type => [type, {
    topicCodes:["1.4.3"], labels:["potęgi"], pointShares:{"1.4.3":1},
    note:"Związek z pierwiastkami: zapis za pomocą potęg o wykładniku wymiernym.",
  }])),
  MaturaSqrtProductPlusPowerClosedTask: {
    topicCodes:["1.1.5"], labels:["pierwiastki"],
    note:"Pomocniczo: obliczanie potęgi o wykładniku ujemnym.",
  },
  MaturaBinomialRootValueClosedTask: {
    topicCodes:["2.1.2", "1.1.5"], labels:["wzory skróconego mnożenia", "pierwiastki"],
  },
  MaturaBasic_SymbolicSquareCubeSixthRootProduct: {
    topicCodes:["1.4.3"], labels:["potęgi"],
    note:"Pierwiastki zapisujemy jako potęgi o wykładniku wymiernym i dodajemy wykładniki.",
    pointShares:{"1.4.3":1},
  },
});

export function getReviewedBasicCourseTopicCodes(type) {
  return REVIEWED_SKILLS[type]?.topicCodes || null;
}

// Reviewed statistical allocation, separate from the CKE score of the task.
// null means unreviewed, not an equal split between every visible topic.
export function getReviewedBasicCoursePointShares(type) {
  return REVIEWED_SKILLS[type]?.pointShares || null;
}

const MONTHS = {marzec:"MAR", maj:"MAJ", czerwiec:"CZE", sierpien:"SIE", grudzien:"GRU"};

export function getBasicCourseVariantSource(task) {
  const source = task?.basicCourseSource;
  const match = /^cke-p-(\d{4})-(\w+)$/.exec(source?.sheetId || "");
  if (!match || !MONTHS[match[2]] || source.taskNo == null) return task?.source;
  return {...task.source, exam:"MATURA", level:"basic", variant:"random",
    session:`${match[1]}_${MONTHS[match[2]]}`, taskNumber:String(source.taskNo)};
}

export function getBasicCourseSkillLabels(task) {
  return task?.basicCourseSource ? REVIEWED_SKILLS[task.type]?.labels || [] : [];
}

export function getBasicCourseSkillNote(task) {
  return task?.basicCourseSource ? REVIEWED_SKILLS[task.type]?.note || "" : "";
}
